import { useCallback, useEffect, useRef, useState } from "react";
import WorkflowCard from "./WorkflowCard";
import InstantWorkflowCard from "./InstantWorkflowCard";
import WorkflowListItem from "./WorkflowListItem";
import { Workflow } from "./interface";
import { getData } from "./backend";
import useDebounce from "./hooks/useDebounce";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, SearchIcon, ViewGridIcon, ViewListIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import { INSTANT_WORKFLOW_SLUGS } from "./utils";

interface Props {
  token: string;
  onSelectWorkflow: (workflow: Workflow) => void;
}

type WorkflowListPageData = {
  workflows: Workflow[];
  instantWorkflows: Workflow[];
  total: number;
  numPages: number;
};

type ViewMode = "CARDS" | "ROWS";
const VIEW_STORAGE_KEY = "portant:workflow-list-view";

function AppWorkflowList(props: Props) {
  const { t } = useTranslation();

  const { token } = props;
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [instantWorkflows, setInstantWorkflows] = useState<Workflow[]>([]);

  const [total, setTotal] = useState(0);

  const [isLoading, setIsLoading] = useState(true);

  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);

  const [error, setError] = useState<Error>();
  const requestSequenceRef = useRef(0);
  const pageCacheRef = useRef<Map<string, WorkflowListPageData>>(new Map());

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "CARDS";
    try {
      const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
      if (stored === "CARDS" || stored === "ROWS") return stored;
    } catch { /* ignore */ }
    return "CARDS";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    } catch { /* ignore */ }
  }, [viewMode]);

  useEffect(() => {
    pageCacheRef.current.clear();
  }, [token]);

  const fetchWorkflows = useCallback(() => {
    if (!token)
      return;

    const requestSequence = ++requestSequenceRef.current;
    const cacheKey = `${token}|${currentPage}|${debouncedSearch}`;
    const cachedPage = pageCacheRef.current.get(cacheKey);
    if (cachedPage) {
      setWorkflows(cachedPage.workflows);
      setTotal(cachedPage.total);
      setNumPages(cachedPage.numPages);
      setInstantWorkflows(cachedPage.instantWorkflows);
      setError(undefined);
      setIsLoading(false);
      return;
    }

    setError(undefined);
    setIsLoading(true);
    const slugs = INSTANT_WORKFLOW_SLUGS.join(",");

    getData(`/hubspot/app/workflows/?page=${currentPage}&slugs=${slugs}&search=${encodeURIComponent(debouncedSearch)}`, token)
      .then(data => {
        const pageData: WorkflowListPageData = {
          workflows: data.results || [],
          total: data.total || 0,
          numPages: data.numPages || 1,
          instantWorkflows: data.instantWorkflows || [],
        };
        pageCacheRef.current.set(cacheKey, pageData);

        if (requestSequence !== requestSequenceRef.current)
          return;

        setWorkflows(pageData.workflows);
        setTotal(pageData.total);
        setNumPages(pageData.numPages);
        setInstantWorkflows(pageData.instantWorkflows);
      })
      .catch((err) => {
        if (requestSequence !== requestSequenceRef.current)
          return;

        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (requestSequence === requestSequenceRef.current)
          setIsLoading(false);
      });
  }, [token, currentPage, debouncedSearch]);

  useEffect(() => {
    fetchWorkflows();
  }, [token, fetchWorkflows]);

  const showContentLoader = isLoading && workflows.length === 0;
  const showInstantTemplates = total <= 4 && search.trim() === "" && search === debouncedSearch && !isLoading;
  const hasResults = workflows.length > 0 || (showInstantTemplates && instantWorkflows.length > 0);

  return (
    <div className="flex flex-col items-center h-full">
      <div className="flex justify-between items-center border-b border-gray-200 pb-4 w-full gap-3">
        <div className="flex items-center gap-8 min-w-0">
          <div className="text-lg font-semibold text-black font-sans leading-tight shrink-0">
            {t("Select Workflow")}
            {total > 0 &&
              <>&nbsp;({total})</>
            }
          </div>
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder={t("Search workflows")}
              className="h-9 w-96 pl-9 pr-3 text-sm font-gilroy text-black border border-gray-200 rounded-md focus:outline-none focus:border-blue placeholder:text-gray-400"
            />
          </div>
        </div>
        <div className="flex items-center gap-6 flex-shrink-0">
          <div className="flex items-center" style={{ visibility: numPages === 0 ? "hidden" : "visible" }}>
            <ChevronLeftIcon
              className={`w-4 h-4 ${currentPage === 1 ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 cursor-pointer"}`}
              onClick={currentPage === 1 ? undefined : () => {
                setError(undefined);
                setIsLoading(true);
                setWorkflows([]);
                setInstantWorkflows([]);
                setCurrentPage(i => Math.max(1, i - 1));
              }}
            />
            <div className="font-semibold text-gray-600 font-gilroy w-12 text-center">
              {currentPage} / {numPages}
            </div>
            <ChevronRightIcon
              className={`w-4 h-4 ${currentPage === numPages ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-gray-600 cursor-pointer"}`}
              onClick={currentPage === numPages ? undefined : () => {
                setError(undefined);
                setIsLoading(true);
                setWorkflows([]);
                setInstantWorkflows([]);
                setCurrentPage(i => Math.min(numPages, i + 1));
              }}
            />
          </div>
          <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
            <button
              className={`flex items-center justify-center w-10 h-10 transition-colors ${viewMode === "CARDS" ? "text-blue bg-blue-50" : "text-gray-400 hover:text-gray-600"}`}
              onClick={() => setViewMode("CARDS")}
              aria-label={t("Card view")}
            >
              <ViewGridIcon className="w-5 h-5" />
            </button>
            <button
              className={`flex items-center justify-center w-10 h-10 transition-colors ${viewMode === "ROWS" ? "text-blue bg-blue-50" : "text-gray-400 hover:text-gray-600"}`}
              onClick={() => setViewMode("ROWS")}
              aria-label={t("List view")}
            >
              <ViewListIcon className="w-5 h-5" />
            </button>
          </div>
          <a
            className="bg-blue hover:bg-blue-10 text-white font-gilroy font-semibold text-sm h-10 w-[164px] px-3 rounded-md flex items-center justify-center gap-1.5 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-colors duration-150"
            href="https://app.portant.co"
            target="_blank"
            rel="noreferrer"
          >
            {t("Open")} Portant
            <ExternalLinkIcon className="h-4 w-4" />
          </a>
        </div>
      </div>

      <div className="flex flex-col items-center overflow-y-auto h-full w-full pt-4">
        {showContentLoader &&
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <img src="https://static.portant.co/portant-loading-gray.svg" alt="Loading Logo" className='w-32 h-32' />
            <div className="text-gray-400 font-semibold text-center text-sm mt-2">{t("Loading Workflows")}...</div>
          </div>
        }
        {(!showContentLoader && !error && hasResults && viewMode === "CARDS") &&
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 min-[1680px]:grid-cols-5 auto-rows-[20rem] gap-4 w-full p-1">
            {workflows.map((workflow) => <WorkflowCard key={workflow.id} workflow={workflow} onSelect={props.onSelectWorkflow} />)}
            {showInstantTemplates && instantWorkflows.map((workflow) => (
              <InstantWorkflowCard key={workflow.id} workflow={workflow} onSelect={props.onSelectWorkflow} />
            ))}
          </div>
        }
        {(!showContentLoader && !error && hasResults && viewMode === "ROWS") &&
          <div className="flex flex-col w-full gap-1">
            {workflows.map((workflow) => <WorkflowListItem key={workflow.id} workflow={workflow} onSelect={props.onSelectWorkflow} />)}
            {showInstantTemplates && instantWorkflows.map((workflow) => (
              <WorkflowListItem key={workflow.id} workflow={workflow} onSelect={props.onSelectWorkflow} isInstant />
            ))}
          </div>
        }
        {(!showContentLoader && !error && !hasResults) &&
          <div className="flex flex-col items-center justify-center h-full gap-1 text-center">
            <div className="text-gray-600 font-gilroy font-semibold">
              {debouncedSearch ? t("No workflows match your search") : t("No workflows yet")}
            </div>
            {debouncedSearch &&
              <div className="text-gray-400 text-sm">{t("Try a different search term")}</div>
            }
          </div>
        }
        {error &&
          <div className="flex flex-col w-full h-full gap-6 text-red text-center font-semibold justify-center">
            {t("Something went wrong. Please contact support.")}
          </div>
        }
      </div>
    </div>
  );
}

export default AppWorkflowList;
